import { useEffect } from 'react'
import { useForm, router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TimeEntryForm } from './TimeEntryForm'
import type { ActivitySubtype, ActivityType, TimeEntry, TimeEntryFormData } from '@/types'

interface Props {
  activitySubtypes: ActivitySubtype[]
  activityTypes: ActivityType[]
  timeEntry: TimeEntry | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function EditModal({ activitySubtypes, activityTypes, timeEntry, open, onOpenChange, onSuccess }: Props) {
  const { t } = useTranslation()

  const { data, setData, patch, processing, errors, reset, clearErrors } = useForm<TimeEntryFormData>({
    activity_subtype_id: null,
    activity_type_id: null,
    date: new Date().toISOString().split('T')[0],
    started_at: null,
    ended_at: null,
    duration_minutes: null,
    notes: null,
  })

  // Update form data when a different entry is selected
  useEffect(() => {
    if (timeEntry && open) {
      const entryHasTimeRange = timeEntry.started_at && timeEntry.ended_at
      setData({
        activity_subtype_id: timeEntry.activity_subtype_id,
        activity_type_id: timeEntry.activity_type_id,
        date: timeEntry.date,
        started_at: timeEntry.started_at,
        ended_at: timeEntry.ended_at,
        duration_minutes: entryHasTimeRange ? null : timeEntry.duration_minutes,
        notes: timeEntry.notes,
      })
      clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeEntry?.id, open])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!timeEntry) return

    patch(`/time_entries/${timeEntry.id}`, {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        onOpenChange(false)
        onSuccess?.()
      },
    })
  }

  const handleDelete = () => {
    if (!timeEntry) return
    if (confirm(t('timeEntries.confirmDelete'))) {
      router.delete(`/time_entries/${timeEntry.id}`, {
        preserveScroll: true,
        onSuccess: () => {
          onOpenChange(false)
          onSuccess?.()
        },
      })
    }
  }

  if (!timeEntry) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('timeEntries.editEntry')}</DialogTitle>
          <DialogDescription>{t('timeEntries.editEntryDescription')}</DialogDescription>
        </DialogHeader>

        <TimeEntryForm
          activitySubtypes={activitySubtypes}
          activityTypes={activityTypes}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          submitLabel={t('common.save')}
          onDelete={handleDelete}
        />
      </DialogContent>
    </Dialog>
  )
}
